import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Boxes, KeyRound, Bike, Clock, Printer } from "lucide-react";
import { autoPrintOn, setAutoPrint } from "@/lib/printReceipt";
import { Switch } from "@/components/ui/switch";
import { usePanel } from "./PanelLayout";
import { errMsg, DAYS } from "@/lib/api";
import { UsersCard } from "@/components/staff/UsersCard";
import { BrandingCard } from "@/components/staff/BrandingCard";
import { VercelExportCard } from "@/components/staff/VercelExportCard";
import { PaymentGatewayCard } from "@/components/staff/PaymentGatewayCard";
import { FonntePanel } from "@/components/staff/FonntePanel";
import { QrPaymentCard } from "@/components/staff/QrPaymentCard";
import { PrinterDevicesCard } from "@/components/staff/PrinterDevicesCard";
import { DailySummaryCard } from "@/components/staff/DailySummaryCard";

const inp = "w-full h-10 px-3 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-[#B91C1C] disabled:bg-slate-50";
const Row = ({ label, hint, children }) => (
  <label className="block space-y-1"><span className="text-sm font-medium">{label}</span>{children}{hint && <span className="block text-xs text-slate-500">{hint}</span>}</label>
);
const Badge = ({ ok }) => <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{ok ? "Kunci API ditetapkan" : "Kunci API belum ditetapkan"}</span>;

function LoyverseTokenField({ hint, onSaved, req }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (value) => {
    setBusy(true);
    try {
      onSaved(await req("put", "/superadmin/loyverse-token", { token: value }));
      setToken("");
      toast.success(value ? "Token Loyverse disimpan" : "Token Loyverse dipadam");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-2" data-testid="loyverse-token-box">
      <p className="text-sm font-medium flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-600" />Token API Loyverse <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400 text-[#3B0A0A] font-bold">SUPERADMIN</span></p>
      <p className="text-xs text-slate-500">{hint ? `Token semasa: ${hint}` : "Belum ada token disimpan."} Dapatkan dari Loyverse Back Office → Integrations → Access tokens.</p>
      <div className="flex gap-2">
        <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Tampal token Loyverse" data-testid="loyverse-token-input" className={inp} autoComplete="off" />
        <button disabled={busy || !token.trim()} onClick={() => save(token)} data-testid="loyverse-token-save"
          className="px-4 h-10 rounded-lg bg-[#B91C1C] text-white text-sm font-semibold shrink-0 active:scale-95 transition-transform disabled:opacity-60">Simpan</button>
        {hint && <button disabled={busy} onClick={() => save("")} data-testid="loyverse-token-clear"
          className="px-3 h-10 rounded-lg border text-sm text-red-600 shrink-0 hover:bg-red-50">Padam</button>}
      </div>
    </div>
  );
}

function WabotKeysField({ s, onSaved, req }) {
  const [id, setId] = useState(s.wabot_instance_hint || "");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      onSaved(await req("put", "/superadmin/wabot", { instance_id: id, access_token: token }));
      setToken("");
      toast.success("Kunci Wabot disimpan");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-2" data-testid="wabot-keys-box">
      <p className="text-sm font-medium flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-600" />Kunci API Wabot <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400 text-[#3B0A0A] font-bold">SUPERADMIN</span></p>
      <p className="text-xs text-slate-500">Dapatkan Instance ID & Access Token dari dashboard Wabot.{s.wabot_token_hint ? ` Token semasa: ${s.wabot_token_hint}` : ""}</p>
      <input value={id} onChange={(e) => setId(e.target.value)} placeholder="Instance ID" data-testid="wabot-instance-input" className={inp} autoComplete="off" />
      <div className="flex gap-2">
        <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder={s.wabot_token_hint ? "Biar kosong jika tidak tukar" : "Access Token"} data-testid="wabot-token-input" className={inp} autoComplete="off" />
        <button disabled={busy} onClick={save} data-testid="wabot-keys-save" className="px-4 h-10 rounded-lg bg-[#B91C1C] text-white text-sm font-semibold shrink-0 active:scale-95 transition-transform disabled:opacity-60">Simpan</button>
      </div>
    </div>
  );
}

function ProviderPicker({ value, onSaved, req }) {
  const pick = async (provider) => {
    try { onSaved(await req("put", "/superadmin/wa-provider", { provider })); toast.success("Penyedia WhatsApp ditukar"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="wa-provider-picker">
      <span className="text-sm font-medium mr-1">Penyedia:</span>
      {[["wabot", "Wabot"], ["fonnte", "Fonnte"]].map(([id, label]) => (
        <button key={id} type="button" onClick={() => value !== id && pick(id)} data-testid={`wa-provider-${id}`}
          className={`px-4 h-9 rounded-full text-sm font-semibold border transition-colors ${value === id ? "bg-[#B91C1C] text-white border-[#B91C1C]" : "bg-white border-slate-200"}`}>{label}</button>
      ))}
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400 text-[#3B0A0A] font-bold">SUPERADMIN</span>
    </div>
  );
}

function FonnteTokenField({ hint, onSaved, req }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { onSaved(await req("put", "/superadmin/fonnte-token", { token })); setToken(""); toast.success("Token Fonnte disimpan"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-2" data-testid="fonnte-token-box">
      <p className="text-sm font-medium flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-600" />Token Peranti Fonnte</p>
      <p className="text-xs text-slate-500">{hint ? `Token semasa: ${hint}. ` : ""}Daftar di fonnte.com → Device → Add device → salin Token.</p>
      <div className="flex gap-2">
        <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Tampal token peranti Fonnte" data-testid="fonnte-token-input" className={inp} autoComplete="off" />
        <button disabled={busy || !token.trim()} onClick={save} data-testid="fonnte-token-save" className="px-4 h-10 rounded-lg bg-[#B91C1C] text-white text-sm font-semibold shrink-0 disabled:opacity-60">Simpan</button>
      </div>
    </div>
  );
}

function AutoPrintCard() {
  const [on, setOn] = useState(autoPrintOn());
  return (
    <section className="bg-white rounded-2xl border p-6 space-y-3" data-testid="settings-autoprint-section">
      <h3 className="font-display font-bold flex items-center gap-2"><Printer className="w-5 h-5 text-red-700" />Cetak Resit Automatik (peranti ini)</h3>
      <label className="flex items-center justify-between gap-4">
        <span className="text-sm">Cetak resit bila pesanan app baharu masuk<span className="block text-xs text-slate-500">Buka Panel ini di tablet/PC kaunter yang disambung ke printer resit dan biarkan terbuka. Loyverse tidak membenarkan resit dari API dicetak terus di POS, jadi resit dicetak dari peranti ini.</span></span>
        <Switch checked={on} onCheckedChange={(v) => { setAutoPrint(v); setOn(v); }} data-testid="settings-autoprint" />
      </label>
    </section>
  );
}

export default function Settings() {
  const { req, me } = usePanel();
  const [s, setS] = useState(null);
  const [meta, setMeta] = useState(null);
  const isAdmin = ["admin", "superadmin"].includes(me.role);
  useEffect(() => { req("get", "/staff/settings").then(setS); }, [req]);
  useEffect(() => {
    if (!isAdmin || !s?.loyverse_configured) return setMeta(null);
    req("get", "/staff/loyverse/meta").then(setMeta).catch((e) => { setMeta(null); toast.error(errMsg(e)); });
  }, [req, isAdmin, s?.loyverse_configured, s?.loyverse_token_hint]);
  if (!s) return <p className="text-sm text-slate-500">Memuatkan...</p>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const fonnte = s.wa_provider === "fonnte";

  const save = async () => {
    try {
      const { wabot_configured, loyverse_configured, ...body } = s;
      setS({ ...(await req("put", "/staff/settings", body)), wabot_configured, loyverse_configured });
      toast.success("Tetapan disimpan");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const testWa = async () => {
    try { await req("post", "/staff/settings/test-wa"); toast.success("Mesej ujian dihantar"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6 max-w-3xl rise">
      <h2 className="font-display text-2xl font-bold">Tetapan</h2>
      <section className="bg-white rounded-2xl border p-6 space-y-4" data-testid="settings-delivery-section">
        <h3 className="font-display font-bold flex items-center gap-2"><Bike className="w-5 h-5 text-red-700" />Penghantaran</h3>
        <label className="flex items-center justify-between gap-4">
          <span className="text-sm">Benarkan pesanan penghantaran<span className="block text-xs text-slate-500">Jika dimatikan, pilihan Penghantaran dikelabukan dan pelanggan hanya boleh Ambil di Restoran.</span></span>
          <Switch disabled={!isAdmin} checked={s.delivery_enabled ?? true} onCheckedChange={(v) => set("delivery_enabled", v)} data-testid="settings-delivery-enabled" />
        </label>
      </section>
      <section className="bg-white rounded-2xl border p-6 space-y-4" data-testid="settings-hours-section">
        <h3 className="font-display font-bold flex items-center gap-2"><Clock className="w-5 h-5 text-red-700" />Waktu Operasi</h3>
        <label className="flex items-center justify-between gap-4">
          <span className="text-sm">Tutup pesanan di luar waktu operasi<span className="block text-xs text-slate-500">Di luar waktu ini pelanggan nampak mesej "Kedai Tutup" dan tidak boleh membuat pesanan.</span></span>
          <Switch disabled={!isAdmin} checked={s.hours_enabled ?? true} onCheckedChange={(v) => set("hours_enabled", v)} data-testid="settings-hours-enabled" />
        </label>
        <div className="grid grid-cols-2 gap-4">
          <Row label="Buka"><input type="time" disabled={!isAdmin} value={s.open_time} onChange={(e) => set("open_time", e.target.value)} data-testid="settings-open-time" className={inp} /></Row>
          <Row label="Tutup"><input type="time" disabled={!isAdmin} value={s.close_time} onChange={(e) => set("close_time", e.target.value)} data-testid="settings-close-time" className={inp} /></Row>
        </div>
        <div className="space-y-2">
          <span className="text-sm font-medium">Hari cuti (tutup sepanjang hari)</span>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d, i) => {
              const off = (s.closed_days || []).includes(i);
              return (
                <button key={d} type="button" disabled={!isAdmin} data-testid={`settings-closed-day-${i}`}
                  onClick={() => set("closed_days", off ? s.closed_days.filter((x) => x !== i) : [...(s.closed_days || []), i])}
                  className={`px-3 h-9 rounded-full text-sm font-medium border transition-colors ${off ? "bg-red-600 text-white border-red-600" : "bg-white border-slate-200"}`}>{d}</button>
              );
            })}
          </div>
        </div>
      </section>
      <section className="bg-white rounded-2xl border p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-display font-bold flex items-center gap-2"><MessageCircle className="w-5 h-5 text-red-700" />Notifikasi WhatsApp ({fonnte ? "Fonnte" : "Wabot"})</h3>
          <Badge ok={s.wa_configured} />
        </div>
        {me.role === "superadmin" && <ProviderPicker value={s.wa_provider} onSaved={(d) => setS((p) => ({ ...p, ...d }))} req={req} />}
        {me.role === "superadmin" && !fonnte && <WabotKeysField s={s} onSaved={(d) => setS((p) => ({ ...p, ...d }))} req={req} />}
        {me.role === "superadmin" && fonnte && <FonnteTokenField hint={s.fonnte_token_hint} onSaved={(d) => setS((p) => ({ ...p, ...d }))} req={req} />}
        {fonnte && isAdmin && <FonntePanel req={req} s={s} set={set} />}
        <label className="flex items-center justify-between"><span className="text-sm">Hantar notifikasi automatik</span><Switch disabled={!isAdmin} checked={s.wa_enabled} onCheckedChange={(v) => set("wa_enabled", v)} data-testid="settings-wa-enabled" /></label>
        <Row label="No. WhatsApp Admin" hint="Format antarabangsa tanpa +, cth: 60123456789">
          <input disabled={!isAdmin} value={s.admin_number} onChange={(e) => set("admin_number", e.target.value)} data-testid="settings-admin-number" className={inp} />
        </Row>
        {!fonnte && <Row label="ID Group Pekerja" hint="Dapatkan dari dashboard Wabot, cth: 120363xxxxxxxx@g.us">
          <input disabled={!isAdmin} value={s.group_id} onChange={(e) => set("group_id", e.target.value)} data-testid="settings-group-id" className={inp} />
        </Row>}
        {!fonnte && <Row label="URL Wabot" hint="Biasanya https://dash.wabot.my">
          <input disabled={!isAdmin} value={s.wabot_base_url} onChange={(e) => set("wabot_base_url", e.target.value)} data-testid="settings-wabot-url" className={inp} />
        </Row>}
        {isAdmin && <button onClick={testWa} data-testid="settings-test-wa" className="px-4 h-10 rounded-lg border border-[#B91C1C] text-[#B91C1C] text-sm font-semibold">Hantar Mesej Ujian</button>}
      </section>
      <section className="bg-white rounded-2xl border p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-display font-bold flex items-center gap-2"><Boxes className="w-5 h-5 text-red-700" />Loyverse POS</h3>
          <Badge ok={s.loyverse_configured} />
        </div>
        {me.role === "superadmin" && <LoyverseTokenField hint={s.loyverse_token_hint} onSaved={(d) => setS((p) => ({ ...p, ...d }))} req={req} />}
        <label className="flex items-center justify-between gap-4">
          <span className="text-sm">Masuk ke Loyverse secara automatik<span className="block text-xs text-slate-500">Jika dimatikan, pekerja masukkan pesanan ke Loyverse secara manual.</span></span>
          <Switch disabled={!isAdmin} checked={s.loyverse_auto} onCheckedChange={(v) => set("loyverse_auto", v)} data-testid="settings-loyverse-auto" />
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <Row label="Kedai (Store)">
            <select disabled={!isAdmin || !meta} value={s.loyverse_store_id} onChange={(e) => set("loyverse_store_id", e.target.value)} data-testid="settings-loyverse-store" className={inp}>
              <option value="">— Pilih —</option>
              {meta?.stores.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </Row>
          <Row label="Jenis Bayaran (Tunai)">
            <select disabled={!isAdmin || !meta} value={s.loyverse_payment_type_id} onChange={(e) => set("loyverse_payment_type_id", e.target.value)} data-testid="settings-loyverse-payment" className={inp}>
              <option value="">— Pilih —</option>
              {meta?.payment_types.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </Row>
          <Row label="Jenis Bayaran (Online)" hint="Untuk pesanan yang dibayar online. Jika kosong, guna jenis Tunai.">
            <select disabled={!isAdmin || !meta} value={s.loyverse_online_payment_type_id || ""} onChange={(e) => set("loyverse_online_payment_type_id", e.target.value)} data-testid="settings-loyverse-online-payment" className={inp}>
              <option value="">— Sama seperti Tunai —</option>
              {meta?.payment_types.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </Row>
          <Row label="Peranti POS (resit dipaparkan di sini)">
            <select disabled={!isAdmin || !meta} value={s.loyverse_pos_device_id || ""} onChange={(e) => set("loyverse_pos_device_id", e.target.value)} data-testid="settings-loyverse-pos" className={inp}>
              <option value="">— Pilih —</option>
              {meta?.pos_devices?.filter((d) => !s.loyverse_store_id || d.store_id === s.loyverse_store_id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </Row>
        </div>
        <p className="text-xs text-slate-500">Padankan setiap menu dengan item Loyverse di halaman Menu. Resit dari app direkod dalam senarai Resit (sejarah jualan) peranti POS yang dipilih. Had Loyverse: resit melalui API tidak dicetak automatik di POS — guna "Cetak Resit Automatik" di bawah.</p>
      </section>
      <AutoPrintCard />
      {isAdmin && <button onClick={save} data-testid="settings-save" className="px-6 h-11 rounded-xl bg-[#B91C1C] text-white font-semibold active:scale-95 transition-transform">Simpan Tetapan</button>}
      {isAdmin && <DailySummaryCard req={req} />}
      {isAdmin && <QrPaymentCard req={req} />}
      {me.role === "superadmin" && <PrinterDevicesCard req={req} />}
      {me.role === "superadmin" && <PaymentGatewayCard req={req} />}
      {isAdmin && <BrandingCard req={req} />}
      {isAdmin && <UsersCard req={req} me={me} />}
      {me.role === "superadmin" && <VercelExportCard />}
    </div>
  );
}
